import React, { useCallback, useRef, useState } from 'react';

import Icon from '@amzn/awsui-components-console/icon';
import Tooltip from '@amzn/awsui-components-console/tooltip';

interface InfoTooltipProps {
  /**
   * Explanatory text shown on hover or focus, and the trigger's `aria-label` —
   * Cloudscape's Tooltip content is not exposed to assistive technologies.
   */
  readonly content: string;
  /** Placement relative to the icon. Defaults to 'top'. */
  readonly position?: 'top' | 'right' | 'bottom' | 'left';
}

/**
 * Shared info icon revealing text on hover or keyboard focus, per Cloudscape's
 * documented Tooltip contract: focusable trigger, owns visibility, dismisses on
 * pointer leave, blur, or Escape, and uses pointer events so touch and pen work.
 *
 * The Tooltip renders inside the trigger span to keep this a single child, which
 * a Fragment would not be inside SpaceBetween.
 */
const InfoTooltip: React.FC<InfoTooltipProps> = ({
  content,
  position = 'top',
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);

  const getTrack = useCallback(() => triggerRef.current, []);
  const show = useCallback(() => setVisible(true), []);
  const hide = useCallback(() => setVisible(false), []);

  return (
    <span
      ref={triggerRef}
      tabIndex={0}
      role="img"
      aria-label={content}
      onPointerEnter={show}
      onPointerLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {/* `size="inherit"` rather than a fixed size: Cloudscape sizes the icon from
          the inherited line height and centres it, so the icon suits its context
          instead of every call site being the same 16px. */}
      <Icon name="status-info" size="inherit" variant="link" />
      {visible && (
        <Tooltip
          content={content}
          getTrack={getTrack}
          position={position}
          onEscape={hide}
        />
      )}
    </span>
  );
};

export default InfoTooltip;
